import React from 'react';
import { Info } from 'lucide-react';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

type NoVisibilityReason = 'twilight' | 'below_horizon' | 'not_visible';

interface HighLatitudeBannerProps {
  latitude: number;
  month: number;
  reason: NoVisibilityReason;
}

const messages: Record<NoVisibilityReason, {
  title: (month: string) => string;
  body: (lat: string) => string;
  suggestion: string;
}> = {
  twilight: {
    title: (month) => `No astronomical darkness in ${month}`,
    body: (lat) =>
      `At ${lat}, the sun doesn't dip far enough below the horizon for full darkness during this month. The persistent twilight glow washes out the Milky Way core.`,
    suggestion: 'Try the shoulder months — March\u2013May or August\u2013October — when true darkness returns at this latitude.',
  },
  below_horizon: {
    title: (month) => `Galactic core below the horizon in ${month}`,
    body: (lat) =>
      `At ${lat}, the Milky Way's galactic core doesn't rise above the horizon during dark hours this month.`,
    suggestion: 'The core is typically visible from late February through October, depending on your latitude.',
  },
  not_visible: {
    title: (month) => `No core visibility in ${month}`,
    body: (lat) =>
      `At ${lat}, the galactic core isn't visible during dark hours this month. This can happen when the core's transit occurs during daylight.`,
    suggestion: 'Check nearby months — the visibility season shifts depending on your latitude and time of year.',
  },
};

export const HighLatitudeBanner: React.FC<HighLatitudeBannerProps> = ({ latitude, month, reason }) => {
  const msg = messages[reason];
  const monthName = MONTH_NAMES[month];
  const latStr = `${Math.abs(latitude).toFixed(1)}\u00B0${latitude > 0 ? 'N' : 'S'}`;

  return (
    <div className="flex gap-3 items-start rounded-lg border border-gold/30 bg-gold/5 p-4 mb-4">
      <Info className="h-5 w-5 text-gold shrink-0 mt-0.5" />
      <div className="text-sm">
        <p className="font-medium text-foreground mb-1">
          {msg.title(monthName)}
        </p>
        <p className="text-muted-foreground leading-relaxed">
          {msg.body(latStr)} {msg.suggestion}
        </p>
      </div>
    </div>
  );
};
